import { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, Star } from 'lucide-react';
import type { ClinicWithDistance } from '@features/clinics/types/clinic';
import type { AccountStatus } from '../types/clientClinic';
import './ClientRelationshipGrid.css';

interface ClientRelationshipGridProps {
  clinics: ClinicWithDistance[];
  selectedClinicId?: number;
  onClinicSelect: (clinicId: number) => void;
  relationshipStatuses?: Map<number, AccountStatus>;
}

type SortDirection = 'asc' | 'desc' | null;
type SortConfig = { key: 'name' | 'distanceMiles'; direction: SortDirection };

function StatusBadge({ status }: { status: AccountStatus }) {
  const className = `status-badge status-${status.toLowerCase()}`;
  return <span className={className}>{status}</span>;
}

export default function ClientRelationshipGrid({
  clinics,
  selectedClinicId,
  onClinicSelect,
  relationshipStatuses = new Map(),
}: Readonly<ClientRelationshipGridProps>) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'distanceMiles',
    direction: 'asc',
  });

  const handleSort = (key: 'name' | 'distanceMiles') => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      if (prev.direction === 'desc') return { key: 'distanceMiles', direction: 'asc' };
      return { key, direction: null };
    });
  };

  const sortedClinics = useMemo(() => {
    if (!sortConfig.direction) return clinics;

    return [...clinics].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [clinics, sortConfig]);

  const SortableHeader = ({
    label,
    sortKey,
  }: {
    label: string;
    sortKey: 'name' | 'distanceMiles';
  }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <th onClick={() => handleSort(sortKey)} className="sortable-header">
        <div className="th-sort-content">
          <span>{label}</span>
          <span className={`sort-chevrons ${isActive ? 'active' : ''}`}>
            <ChevronUp
              size={12}
              className={`sort-icon ${isActive && sortConfig.direction === 'asc' ? 'active' : ''}`}
            />
            <ChevronDown
              size={12}
              className={`sort-icon ${isActive && sortConfig.direction === 'desc' ? 'active' : ''}`}
            />
          </span>
        </div>
      </th>
    );
  };

  return (
    <div className="client-relationship-grid">
      <table className="data-table">
        <thead>
          <tr>
            <SortableHeader label="Clinic Name" sortKey="name" />
            <SortableHeader label="Distance" sortKey="distanceMiles" />
            <th>Rating</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {sortedClinics.length === 0 ? (
            <tr>
              <td colSpan={4} className="empty-message">
                No clinics found in this area
              </td>
            </tr>
          ) : (
            sortedClinics.map((clinic) => {
              const status = relationshipStatuses.get(clinic.id);
              return (
                <tr
                  key={clinic.id}
                  className={selectedClinicId === clinic.id ? 'selected' : ''}
                  onClick={() => onClinicSelect(clinic.id)}
                >
                  <td className="clinic-name-cell">
                    <div className="clinic-name">{clinic.name}</div>
                    <div className="clinic-location">
                      {clinic.city}, {clinic.state}
                    </div>
                  </td>
                  <td className="distance-cell">{clinic.distanceMiles != null ? `${clinic.distanceMiles.toFixed(1)} mi` : '—'}</td>
                  <td className="rating-cell">
                    <div className="star-rating">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} size={14} className="star-empty" />
                      ))}
                    </div>
                  </td>
                  <td className="status-cell">
                    {status ? <StatusBadge status={status} /> : <span className="no-status">-</span>}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
